import { useMemo } from 'react';
import type { ContentWithPhoto } from '../../../../../types/MiniSiteContents';
import ContentWrapper from '../../../common/ContentWrapper';
import ImageWithFallback from '../../../common/ImageWithFallback';
import isAbsoluteUrl from '../../../../core/helpers/isAbsoluteUrl';
import useRelativeUrl from '../../../../core/hooks/useRelativeUrl';
import Markdown from '../../../common/Markdown/Markdown';

type Props = {
  miniSiteContent: ContentWithPhoto;
};

const MiniSiteContentWithPhoto = ({ miniSiteContent }: Props) => {
  const { contentTitle, title, description, photoUrl, photoPosition, link } = miniSiteContent;
  const { getRelativeUrl } = useRelativeUrl();

  const Photo = useMemo(() => {
    return (
      <div key="photo" className="relative w-1/2 md:col-span-3 md:w-full">
        <ImageWithFallback
          alt="content-with-photo-image"
          className="rounded-lg"
          src={photoUrl || '/fallback-img-1440x440.png'}
          layout="fill"
          objectFit="cover"
          sizes="(max-width: 768px) 100vw, 640px"
          fallbackSrc="/fallback-img-1440x440.png"
          url={link && (isAbsoluteUrl(link) ? link : getRelativeUrl(link))}
          target={isAbsoluteUrl(link) ? '_blank' : '_self'}
        />
      </div>
    );
  }, [photoUrl, link, getRelativeUrl]);

  const Content = useMemo(
    () => (
      <div
        key="content"
        className="flex w-1/2 flex-col gap-2.5 rounded-lg bg-white p-10 md:col-span-4 md:w-full"
      >
        <p className="font-bold">{title}</p>
        <Markdown className="text-small">{description}</Markdown>
      </div>
    ),
    [description, title]
  );

  const renderContents = () => {
    if (photoPosition === 'left') return [Photo, Content];

    return [Content, Photo];
  };

  return (
    <>
      {/* desktop view */}
      <ContentWrapper className="hidden w-full lg:block lg:flex-col">
        {contentTitle && <p className="pb-6 text-title3 font-bold">{contentTitle}</p>}
        <div className="grid min-h-[257px] grid-cols-7 gap-8">{renderContents()}</div>
      </ContentWrapper>

      {/* mobile view */}
      <div className="flex flex-col lg:hidden">
        <ContentWrapper className="w-full">
          {contentTitle && <p className="pb-6 text-title3 font-bold">{contentTitle}</p>}
        </ContentWrapper>
        <div>
          <div className="absolute w-full">
            <div className="relative h-56 sm:h-100">
              <ImageWithFallback
                alt="content-with-photo-image"
                src={miniSiteContent.photoUrl || '/fallback-img-1440x440.png'}
                layout="fill"
                sizes="(max-width: 768px) 100vw, 640px"
                objectFit="cover"
                fallbackSrc="/fallback-img-1440x440.png"
              />
            </div>
          </div>
          <ContentWrapper className="relative top-48 mb-48 flex w-full justify-center sm:top-84 sm:mb-84">
            <div className="flex flex-col gap-2.5 rounded-lg bg-white p-6 sm:p-10">
              <p className="text-center font-bold sm:text-left">{miniSiteContent.title}</p>
              <Markdown className="text-small">{miniSiteContent.description}</Markdown>
            </div>
          </ContentWrapper>
        </div>
      </div>
    </>
  );
};

export default MiniSiteContentWithPhoto;
